import React from 'react';
import { observer } from 'mobx-react';
import { SettingsBillingWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import styled from '@emotion/styled';
import Text from 'components/text';
import Button from 'components/button/button';
import { Theme } from '@seedlang/constants';
import OutlineButton from 'components/button/outline_button';
import Spinner from 'components/spinner';
import { flexLeftColumn } from '@seedlang/style_mixins';
import { computed, observable } from 'mobx';
import PageTitle from 'components/common/page_title';
import Stack from 'components/layout/stack';
import MobileSubscriptionBanner from 'components/membership/mobile_subscription_banner';
import { UndoLinkButton, SmallSpinner } from './settings_billing_memrev';
import Modal from 'components/modal';
import PaymentHistory from './payment_history';

const Wrapper = styled.div`
  ${flexLeftColumn()};
  align-items: stretch;
  gap: 20px;
`;

const ButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
`;

const CancellationNotice = styled.div`
  color: ${Theme.gray};
  font-size: 14px;
`;

const ContactModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  p {
    margin: 0;
  }
`;

@observer
class SettingsBilling extends React.Component {
  @observable portalLoadingForSubscription = null;
  @observable showContactModal = false;
  @computed get activeSubscriptionUsesGermanStripeAccount() {
    return this.props.activeSubscription?.stripeAccount === 'DE';
  }

  onOpenSubscriptionPortal = async (subscription, actionType) => {
    if (subscription?.stripeAccount === 'DE') {
      this.showContactModal = true;
      return;
    }

    this.portalLoadingForSubscription = subscription.id;
    try {
      window.location.href = await AppUI.stripeStore.getCustomerPortalUrl({
        return_url: window.location.href,
        action_type: actionType,
        stripe_account: subscription.stripeAccount,
        stripe_customer_id: subscription.stripeCustomerId,
        stripe_subscription_id: subscription.stripeSubscriptionId,
      });
    } catch {
      this.portalLoadingForSubscription = null;
    }
  }

  @computed get completedPaymentMigration() {
    return AppUI.user.paymentMigration?.status === 'completed';
  }

  render() {

    if (!AppUI.userIsLoaded) {
      return (
        <Wrapper>
          <PageTitle>Manage Subscription</PageTitle>
          <Text heading='3'>Manage Subscription</Text>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <PageTitle>Manage Subscription</PageTitle>
        <Text heading='3'>Manage Subscription</Text>
        {
          !AppUI.user.isMember &&
          <Text bold>You don't have an active subscription.</Text>
        }
        {
          this.showContactModal &&
          <Modal
            onCloseModal={() => this.showContactModal = false}
            width='100%'
            maxWidth='520px'
            title='Please contact us'
            background={Theme.blue}
            contentBackground='#FFF'
          >
            <ContactModalBody>
              <p>
                You have been with Seedlang for a very long time!
              </p>
              <p>
                We have updated our payment infrastructure, and we will need you to enter your payment details again.
              </p>
              <p>
                Please write to us about what change you would like to your subscription, and we will get back to you as quick as we can.
              </p>
              <p>
                As a thank you, we will also offer you a 10% discount for the first year of your subscription on the new system.
              </p>
              <Button
                to='/contact'
                background={Theme.blue}
                width='100%'
              >
                Go to Contact Form
              </Button>
            </ContactModalBody>
          </Modal>
        }
        <MobileSubscriptionBanner userSubscriptions={this.props.userSubscriptions} />
        {
          AppUI.user.currentUserGroup?.freeMembership &&
          <div>
            You currently have a free <strong>{AppUI.user.currentGroupMembershipType?.name}</strong>
            {AppUI.user.currentUserGroup.freeMembershipUntil?.date
              ? ` until ${AppUI.user.currentUserGroup.freeMembershipUntil?.formattedDateWithYear}`
              : '.'}
          </div>
        }
        {
          (AppUI.user.permanentMembership && !AppUI.user.mobilePaymentProblem) &&
          <Text color={Theme.green} bold>
            You have a free membership. Congrats!
          </Text>
        }
        {
          (AppUI.user.temporaryFreeMembership && !AppUI.user.mobilePaymentProblem) &&
          <Text color={Theme.green} bold>
            Congrats, you have a free membership! It will expire on {AppUI.user.memberUntil.formattedDateWithYear}.
          </Text>
        }
        {
          AppUI.user.mobilePaymentProblem &&
          <Text color={Theme.green} bold>
            Congrats, you have a membership!
          </Text>
        }
        {
          AppUI.user.isMember && this.props.activeSubscription &&
          <>
            {
              isPresent(this.props.subscriptionStatusText) &&
              <Text bold>
                <Stack gap='xs'>
                  {this.props.subscriptionStatusText}
                  {
                    isPresent(this.props.activeSubscription?.nextPhaseMembership?.title) &&
                      (this.portalLoadingForSubscription === this.props.activeSubscription?.id
                        ? <SmallSpinner background={Theme.darkestBlue} />
                        : <UndoLinkButton onClick={() => this.onOpenSubscriptionPortal(this.props.activeSubscription, 'release')}>
                            Cancel change
                          </UndoLinkButton>
                      )
                  }
                </Stack>
              </Text>
            }
            {
              this.props.activeSubscription.oneTimePayment &&
              <Text bold>
                You have a lifetime membership. You are awesome!{' '}
                <span role='img' aria-label='celebration'>
                  🎉🎉🎉
                </span>
              </Text>
            }
            {
              this.props.activeSubscription.pending && !this.props.activeSubscription.oneTimePayment &&
              <Text bold>
                <span role='img' aria-label='warning'>
                  ⚠️
                </span>{' '}
                Please note that PayPal takes 24 hours to send the initial payment. If you wish to cancel your subscription, please do so after the 24 hours has passed.
              </Text>
            }
          </>
        }
        <ButtonsSection>
          <ButtonRow>
            {
              !this.props.activeSubscription?.oneTimePayment && !this.props.activeSubscriptionIsMobile &&
              <OutlineButton
                to={this.props.activeStripeSubscriptionExists ? null : 'memberships.index'}
                selectedBackground={Theme.blue}
                onClick={this.props.activeStripeSubscriptionExists ? () => this.onOpenSubscriptionPortal(this.props.activeSubscription) : null}
                borderRadius='20px'
                fontSize='16px'
                fontWeight='800'
                height='40px'
                padding='0 20px'
              >
                Update Your Subscription
              </OutlineButton>
            }
            {
              this.props.showCancelButton && !this.props.activeSubscriptionIsMobile &&
              <OutlineButton
                selectedBackground={Theme.red}
                to={this.activeSubscriptionUsesGermanStripeAccount ? null : 'settings.cancel'}
                onClick={this.activeSubscriptionUsesGermanStripeAccount ? () => this.showContactModal = true : null}
                borderRadius='20px'
                fontSize='16px'
                fontWeight='800'
                height='40px'
                padding='0 20px'
              >
                Cancel Membership
              </OutlineButton>
            }
          </ButtonRow>
          {
            this.props.showCancelButton && !this.props.activeSubscriptionIsMobile &&
            <CancellationNotice>
              {this.props.cancellationNoticeText}
            </CancellationNotice>
          }
        </ButtonsSection>
        <PaymentHistory
          userSubscriptions={this.props.userSubscriptions}
          activeSubscription={this.props.activeSubscription}
        />
      </Wrapper>
    );
  }
}

export default SettingsBillingWrapper(SettingsBilling);
